import React, { memo, useMemo, useCallback, useEffect } from 'react'
import { Alert, Col, Row, Popover } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import { useForm } from 'antd/lib/form/Form'
import { extract } from '../../../../../_utils/object'
import { canShowField } from '../helpers'
import { KeywordsSection } from './keywords-section'
import { PushlyAiResultsProps, SuggestedFieldsForm, VariantAction } from '../types'
import { PushlyAi } from '@pushly/aqe/lib/components'
import { VariantCard } from './variant-card'

const MAX_KEYWORDS = 20

/**
 * PushlyAiResults component
 * Handles Pushly AI variant display and selection logic including:
 * - Grid of VariantCard components
 * - Unified variant state management with enable/disable and action selection
 * - Cross-variant awareness for replace actions
 * - Batch variant operations
 */
const PushlyAiResults = memo(
    ({
        domain,
        results,
        builder,
        variantStates,
        selectedKeywords,
        setSelectedKeywords,
        onVariantEnabledChange,
        onVariantActionChange,
        onFieldSelectionsChange,
    }: PushlyAiResultsProps) => {
        const [keywordForm] = useForm<SuggestedFieldsForm>()

        // Extract keywords from the first variant (assuming all variants have similar keywords)
        const allKeywords = useMemo<string[]>(() => {
            if (!results || results.length === 0) return []
            const firstVariant = results[0]
            const keywords = (firstVariant ? extract(firstVariant, 'keywords', []) : []) as string[]
            // Normalize keywords to lowercase and sort
            const normalized = keywords.map((kw) => kw.toLowerCase())
            return normalized.sort().slice(0, MAX_KEYWORDS)
        }, [results])

        // Initialize selected keywords
        const hasKwSuggestions = allKeywords.length > 0

        // Initialize form checkbox based on whether keywords are selected
        useEffect(() => {
            if (hasKwSuggestions && selectedKeywords !== null) {
                const allSelected = selectedKeywords.length === allKeywords.length
                keywordForm.setFieldsValue({ keywords: allSelected })
            }
        }, [hasKwSuggestions, selectedKeywords, allKeywords.length, keywordForm])

        // Memoized keyword selection state
        const keywordSelectionState = useMemo(() => {
            const totalSuggestedKws = allKeywords.length
            const totalSelectedKws = selectedKeywords?.length ?? 0
            const allKwSelection = hasKwSuggestions && totalSelectedKws === totalSuggestedKws
            const partialKwSelection = hasKwSuggestions && !allKwSelection && totalSelectedKws > 0

            return {
                totalSuggestedKws,
                totalSelectedKws,
                allKwSelection,
                partialKwSelection,
            }
        }, [hasKwSuggestions, allKeywords.length, selectedKeywords?.length])

        // Update form when keyword selection state changes
        useEffect(() => {
            if (hasKwSuggestions) {
                keywordForm.setFieldsValue({
                    keywords: keywordSelectionState.allKwSelection,
                })
            }
        }, [keywordSelectionState.allKwSelection, hasKwSuggestions, keywordForm])

        // Memoized keyword change handler
        const handleKeywordChange = useCallback((kw: string, checked: boolean) => {
            setSelectedKeywords((prev) => {
                const currentSelected = prev ?? []
                return checked ? [...currentSelected, kw] : currentSelected.filter((k: string) => k !== kw)
            })
        }, [])

        // Memoized main keyword checkbox handler
        const handleMainKeywordChange = useCallback(
            ({ target: { checked } }: { target: { checked: boolean } }) => {
                const newSelection = checked ? allKeywords : []
                setSelectedKeywords(newSelection)
                keywordForm.setFieldsValue({ keywords: checked })
            },
            [allKeywords],
        )

        // Cross-variant awareness: compute disabled replace actions
        const disabledReplaceActions = useMemo(() => {
            const disabled: number[] = []

            Object.values(variantStates || {}).forEach((state) => {
                if (state.enabled && state.selectedAction?.type === 'replace') {
                    disabled.push(state.selectedAction.targetIndex)
                }
            })

            return disabled
        }, [variantStates])

        // Event handlers for new unified state model
        const handleEnabledChange = useCallback(
            (variantIndex: number, enabled: boolean) => {
                onVariantEnabledChange(variantIndex, enabled)
            },
            [onVariantEnabledChange],
        )

        const handleActionChange = useCallback(
            (variantIndex: number, action: VariantAction | null) => {
                onVariantActionChange(variantIndex, action)
            },
            [onVariantActionChange],
        )

        const variantCalculations = useMemo(() => {
            const currentVariantCount = builder?.variants?.length ?? 1 // At least 1 variant exists
            const maxVariants = 4

            // Calculate from new unified state
            const enabledVariants = Object.values(variantStates || {}).filter((v) => v.enabled)
            const selectedAddVariantsCount = enabledVariants.filter((v) => v.selectedAction?.type === 'add').length
            const hasReplaceSelection = enabledVariants.some((v) => v.selectedAction?.type === 'replace')
            const hasAddSelections = selectedAddVariantsCount > 0

            const canAddMoreVariants = currentVariantCount + selectedAddVariantsCount < maxVariants
            const hasResults = results && results.length > 0

            return {
                currentVariantCount,
                maxVariants,
                selectedAddVariantsCount,
                canAddMoreVariants,
                hasResults,
                hasReplaceSelection,
                hasAddSelections,
                enabledVariants,
            }
        }, [builder?.variants?.length, variantStates, results])

        // Pre-compute "other add actions count" for each variant (optimization)
        const addActionsByVariant = useMemo(() => {
            // Build a map of: variantIndex -> count of OTHER variants with "add" selected
            const map: Record<number, number> = {}

            // First, collect all indices with "add" action
            const addActionIndices = Object.entries(variantStates || {})
                .filter(([, state]) => state.enabled && state.selectedAction?.type === 'add')
                .map(([idx]) => parseInt(idx, 10))

            // For each variant, count how many OTHER variants have "add"
            results.forEach((_, index) => {
                map[index] = addActionIndices.filter((idx) => idx !== index).length
            })

            return map
        }, [variantStates, results])

        if (!variantCalculations.hasResults) {
            return (
                <div className="ai-instructions-section">
                    <div className="instructions-text">
                        <Alert
                            type="warning"
                            showIcon={true}
                            icon={<PushlyAi />}
                            message={
                                <span>
                                    We were unable to generate any notification variants based on the provided Landing
                                    URL. Please try again later or with a different Landing URL.
                                </span>
                            }
                        />
                    </div>
                </div>
            )
        }

        return (
            <>
                <div className="ai-instructions-section">
                    <div className="instructions-text">
                        <PushlyAi /> We have generated the following notification variants based on the provided Landing
                        URL. Please review each variant and select which ones you would like to use by choosing "Replace
                        current" to update your existing notification or "Add as new" to create additional A/B test
                        variants.
                    </div>
                </div>

                <div className="ai-variants-cards">
                    <Row gutter={[16, 16]}>
                        {results.map((variant, index) => {
                            const variantState = variantStates?.[index]
                            const isEnabled = variantState?.enabled ?? false
                            const selectedAction = variantState?.selectedAction ?? null

                            // Use pre-computed count from the map (optimization)
                            const otherAddActionsCount = addActionsByVariant[index]
                            const effectiveVariantCount = variantCalculations.currentVariantCount + otherAddActionsCount

                            return (
                                <Col key={index} xs={24} sm={12} lg={12} xl={12}>
                                    <VariantCard
                                        variant={variant}
                                        variantIndex={index}
                                        domain={domain}
                                        isEnabled={isEnabled}
                                        selectedAction={selectedAction}
                                        disabledReplaceActions={disabledReplaceActions}
                                        maxVariants={variantCalculations.maxVariants}
                                        currentVariantCount={effectiveVariantCount}
                                        builderVariantCount={variantCalculations.currentVariantCount}
                                        onEnabledChange={handleEnabledChange}
                                        onActionChange={handleActionChange}
                                        onFieldSelectionsChange={onFieldSelectionsChange}
                                    />
                                </Col>
                            )
                        })}
                    </Row>
                </div>

                {hasKwSuggestions && canShowField(domain, 'keywords') && (
                    <KeywordsSection
                        form={keywordForm}
                        keywords={allKeywords}
                        selectedKeywords={selectedKeywords}
                        onKeywordChange={handleKeywordChange}
                        onMainCheckboxChange={handleMainKeywordChange}
                        title={
                            <>
                                <span>Keywords</span>
                                <Popover
                                    content="Selected keywords are applied to all created and replaced notifications."
                                    trigger="hover"
                                >
                                    <InfoCircleOutlined className="info-icon" />
                                </Popover>
                            </>
                        }
                        className="shared-keywords-section"
                        allSelected={keywordSelectionState.allKwSelection}
                        partialSelected={keywordSelectionState.partialKwSelection}
                    />
                )}
            </>
        )
    },
)

PushlyAiResults.displayName = 'PushlyAiResults'

export default PushlyAiResults
